import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { ActivatedRoute } from '@angular/router';
import { tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { MessageEditComponent } from 'src/app/messages/message-edit/message-edit.component';
import { UtilsService } from 'src/app/shared/utils.service';
import { ApiDataSource } from '../../../../utils/api-data-source';
import { ApiRequestService } from '../../../../shared/api-request.service';
import { NetworkedUsersSelectorComponent } from '../../../../shared/networked-users-selector/networked-users-selector.component';
import { UserPublicProfileComponent } from '../../../../shared/user-public-profile/user-public-profile.component';
import { RBACService } from '../../../../shared/rbac.service';

@Component({
  selector: 'app-sites-eep-safety-managers',
  templateUrl: './sites-eep-safety-managers.component.html',
  styleUrls: ['./sites-eep-safety-managers.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SitesEepSafetyManagersComponent implements OnInit, AfterViewInit {
  // Store the site id for use later on.
  @Input('site_id') site_id: number;

  // The columns to display.
  displayedColumns: string[] = [
    'select',
    'employer',
    'contact_person',
    'email',
    'mobile',
    'trade',
    'actions'
  ];

  // Define the data source to use for the table.
  dataSource: SitesSafetyManagersDataSource = new SitesSafetyManagersDataSource(
    this.app,
    this.api
  );

  // The paginator and sorter
  @ViewChild('paginator1') paginator1: MatPaginator;
  @ViewChild('paginator2') paginator2: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  // RBAC permission flags. Resolved once in ngOnInit() so the template does not
  // re-evaluate permission lookups on every change detection cycle.
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;
  canCreateMessages: boolean = false;

  constructor(
    public app: AppService,
    public utils: UtilsService,
    public route: ActivatedRoute,
    private api: ApiRequestService,
    public rbac: RBACService
  ) {}

  ngOnInit(): void {
    // Resolve the RBAC permission flags first so they are always set, even when
    // the access guard below short circuits the rest of this method.
    this.canView = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_SITES_EEP,
        RBACService.RBAC_A.A_VIEW
      )
    ]);
    this.canEdit = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_SITES_EEP,
        RBACService.RBAC_A.A_EDIT
      )
    ]);
    this.canDelete = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_SITES_EEP,
        RBACService.RBAC_A.A_DELETE
      )
    ]);
    this.canCreateMessages = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_MESSAGES,
        RBACService.RBAC_A.A_CREATE
      )
    ]);

    if (!this.canView) {
      return;
    }

    // Load the data if the site id is valid.
    if (this.site_id) {
      // Add the site id to the data source object.
      this.dataSource.site_id = this.site_id;
      // Load the list data.
      this.dataSource.getData(true);
    }
  }

  ngAfterViewInit(): void {
    if (!this.canView) {
      return;
    }

    // Reset the paginator when sorting takes place.
    this.sort.sortChange.subscribe(
      (): void => {
        this.paginator1.pageIndex = 0;
        this.paginator2.pageIndex = 0;
      }
    );

    // Subscribe to the sorter tap events.
    this.sort.sortChange
      .pipe(
        tap(
          (sorter: Sort): void => {
            this.dataSource.order_by = sorter['active'];
            this.dataSource.order = sorter['direction'];
            this.dataSource.getData(true);
          }
        )
      )
      .subscribe();

    // Define and subscribe to the paginator events.
    const paginatorTap = tap(
      (paginator): void => {
        this.paginator1.pageIndex = paginator['pageIndex'];
        this.paginator1.pageSize = paginator['pageSize'];
        this.paginator2.pageIndex = paginator['pageIndex'];
        this.paginator2.pageSize = paginator['pageSize'];

        this.dataSource.limit = paginator['pageSize'];
        this.dataSource.offset = paginator['pageIndex'];

        this.dataSource.getData();
      }
    );

    // Subscribe to the paginator tap events.
    this.paginator1.page.pipe(paginatorTap).subscribe();
    this.paginator2.page.pipe(paginatorTap).subscribe();
  }

  /**
   * Open the networked users selector to manage the list of site safety managers.
   */
  onManageSiteSafetyManagers(): void {
    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_EEP,
          RBACService.RBAC_A.A_EDIT
        )
      ])
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    this.utils
      .showComponentDialog(
        NetworkedUsersSelectorComponent,
        {
          selected: this.dataSource.allIds,
          multiple: true,
          visitor_site_id: this.dataSource.site_id
        },
        {
          width: '1200px'
        }
      )
      .then(
        (user_ids: number[]): void => {
          if (user_ids && user_ids.length > 0) {
            this.api
              .makeRequest(
                'post',
                `v2/sites/${this.dataSource.site_id}/safety-managers`,
                {
                  user_ids: user_ids
                }
              )
              .then(
                (response: any): void => {
                  // Reload the list of site safety managers.
                  this.dataSource.getData();
                }
              );
          }
        }
      );
  }

  /**
   * Remove one selected site safety manager.
   * @param evt
   * @param user_id
   */
  onRemoveSiteSafetyManager(evt: any, user_id: number): void {
    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_EEP,
          RBACService.RBAC_A.A_DELETE
        )
      ])
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    this.utils.showQuickActions(
      evt.target,
      'Are you sure you want to remove the safety manager?',
      [
        {
          text: 'No',
          handler: (): void => {}
        },
        {
          text: 'Yes',
          handler: (): void => {
            this.removeSiteSafetyManagers([user_id]);
          }
        }
      ]
    );
  }

  /**
   * Remove multiple site safety managers.
   * @param evt
   */
  onRemoveSelectedSiteSafetyManagers(evt: any): void {
    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_EEP,
          RBACService.RBAC_A.A_DELETE
        )
      ])
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    this.utils.showQuickActions(
      evt.target,
      'Are you sure you want to remove the selected safety managers?',
      [
        {
          text: 'No',
          handler: (): void => {}
        },
        {
          text: 'Yes',
          handler: (): void => {
            this.removeSiteSafetyManagers(this.dataSource.selection.selected);
          }
        }
      ]
    );
  }

  /**
   * Remove selected site safety managers.
   * @param user_ids
   * @private
   */
  private removeSiteSafetyManagers(user_ids: number[]): void {
    if (user_ids && user_ids.length > 0) {
      this.api
        .makeRequest(
          'delete',
          `v2/sites/${this.dataSource.site_id}/safety-managers/` +
            user_ids.join(',')
        )
        .then(
          (response: any): void => {
            this.utils.showToast('The site safety manager(s) were removed.');
            // Reload the list of site managers.
            this.dataSource.getData();
            // Clear the selection if any.
            this.dataSource.selection.clear();
          }
        )
        .catch(
          (errorResponse: any): void => {
            this.utils.showModal(
              'Error Message',
              'The safety manager(s) could not be removed. Please try again.'
            );
          }
        );
    }
  }

  /**
   * Compose a new message for the site safety managers.
   */
  onComposeNewMessage(user_id: number = null): void {
    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_MESSAGES,
          RBACService.RBAC_A.A_CREATE
        )
      ])
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    // Check if the user has selected site safety managers.
    if (this.dataSource.selection.selected.length == 0 && !user_id) {
      this.utils.showToast(
        'Please select safety managers to send the message to.'
      );
      return;
    }

    // Open the message composer.
    this.utils
      .showComponentDialog(
        MessageEditComponent,
        {
          preSelectUserIds: user_id
            ? [user_id]
            : this.dataSource.selection.selected
        },
        {
          width: '1200px'
        }
      )
      .then(
        (response: any): void => {
          if (response) {
            this.utils.showToast(
              'You can view the message stats in the messages section.'
            );
          }
        }
      );
  }

  onUserPublicView(hash: string): void {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      {
        width: '90%'
      },
      (): void => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData();
      }
    );
  }

  protected readonly RBACService = RBACService;
}

export class SitesSafetyManagersDataSource extends ApiDataSource {
  // The site id to use
  site_id: number;

  // The field to order results by and the order direction.
  order_by: string = 'contact_person';
  order: string = 'asc';

  getData(resetOffset: boolean = false) {
    // Do not load any data if the site id is not present.
    if (!this.site_id) {
      return;
    }

    return this.makeRequest(
      `v2/sites/${this.site_id}/safety-managers`,
      resetOffset
    );
  }
}
